/* assets/sticky-add-to-cart.js
   Barra sticky de Add to Cart para Dawn (solo botón).
   No reimplementa la lógica de variantes/carrito: localiza el <form> real
   de Add to Cart de la sección de producto y "hace click" en su botón,
   para que el flujo AJAX nativo de Dawn (product-form.js) se ejecute igual
   que si el usuario hubiera pulsado el botón original. El estado de carga
   (spinner, disabled) se sincroniza reflejando el botón real, no con un
   temporizador manual.

   Visibilidad: el botón sticky se muestra solo cuando se cumplen A Y B:
   A) el botón real de "Añadir al carrito" ya salió por arriba del viewport.
   B) el footer (.vf-footer) todavía NO ha entrado en pantalla.
*/

const FOOTER_SELECTOR = '.vf-footer';

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.button = this.querySelector(`#StickyAddToCart-Button-${this.sectionId}`);
    this.spinner = this.button ? this.button.querySelector('.loading__spinner') : null;
    this.textEl = this.button ? this.button.querySelector('.sticky-atc__button-text') : null;

    // Estado de las dos condiciones de visibilidad.
    this._pastAddToCartButton = false;
    this._footerInView = false;
  }

  connectedCallback() {
    this.mainSection = document.querySelector(`#MainProduct-${this.sectionId}`) || document.querySelector('main');

    // Ajusta estos selectores si tu tema usa otros nombres de clase/id.
    this.realForm = this.mainSection.querySelector('form[action*="/cart/add"]');
    this.realSubmitButton = this.realForm
      ? this.realForm.querySelector('[name="add"], .product-form__submit')
      : null;

    this.observeTarget = this.realSubmitButton || this.realForm;
    this.footer = document.querySelector(FOOTER_SELECTOR);

    this._bindEvents();
    this._observeAddToCartVisibility();
    this._observeFooterVisibility();
    this._observeButtonState();
  }

  _bindEvents() {
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      if (!this.realSubmitButton || this.realSubmitButton.disabled) return;

      // Feedback inmediato: el estado real lo confirma/corrige el
      // MutationObserver en cuanto Dawn actualice el botón real.
      this._setLoading(true);

      // Dispara el click real: reutiliza toda la lógica AJAX de Dawn
      // (product-form.js), incluyendo el spinner, la notificación/drawer
      // de carrito y el manejo de errores.
      this.realSubmitButton.click();
    });
  }

  _setLoading(isLoading) {
    if (!this.button) return;
    this.button.classList.toggle('loading', isLoading);
    this.button.disabled = isLoading || (this.realSubmitButton && this.realSubmitButton.disabled);
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
  }

  _updateVisibility() {
    const shouldShow = this._pastAddToCartButton && !this._footerInView;
    this.classList.toggle('sticky-atc--visible', shouldShow);
  }

  _observeAddToCartVisibility() {
    if (!this.observeTarget || !('IntersectionObserver' in window)) {
      // Sin soporte o sin botón real de referencia: se apoya solo en el footer.
      this._pastAddToCartButton = true;
      this._updateVisibility();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // entry.boundingClientRect.bottom < 0  =>  el botón real ya salió
          // por completo por ARRIBA del viewport (usuario scrolleó hacia abajo).
          this._pastAddToCartButton = entry.boundingClientRect.bottom < 0;
          this._updateVisibility();
        });
      },
      { root: null, threshold: 0 }
    );

    observer.observe(this.observeTarget);
  }

  _observeFooterVisibility() {
    if (!this.footer || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this._footerInView = entry.isIntersecting;
          this._updateVisibility();
        });
      },
      { root: null, threshold: 0 }
    );

    observer.observe(this.footer);
  }

  _observeButtonState() {
    if (!('MutationObserver' in window) || !this.realSubmitButton || !this.button) return;

    const sync = () => {
      const isLoading = this.realSubmitButton.classList.contains('loading');
      this._setLoading(isLoading);

      const realTextEl = this.realSubmitButton.querySelector('span');
      if (this.textEl && realTextEl) {
        this.textEl.textContent = realTextEl.textContent.trim();
      }
    };

    const buttonObserver = new MutationObserver(sync);
    buttonObserver.observe(this.realSubmitButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Estado inicial por si el botón real ya está disabled (p. ej. sin stock).
    sync();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
